// QuestionSetRewards.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Badge,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown
} from "reactstrap";
import { withTranslation } from "react-i18next";

import axiosInstance from "utils/axiosInstance";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Odometer from 'react-odometerjs';

import { useDispatch } from "react-redux";

import { uploadImage, fetchImage } from "utils/images/imageManager";
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

import home4 from 'assets/images/info/home4.png';
import placeholder_image from "assets/images/info/card-placeholder-medium.png";
import { FaStar, FaRegStar } from 'react-icons/fa';
import { Star } from 'lucide-react';
import { ArrowLeft, MessageSquareText, CircleChevronRight, CheckCheck, RefreshCcw,
  TicketCheck, ListTodo } from 'lucide-react';
import { MoreVertical, Edit2, Trash2 } from 'react-feather';

import { useAutoFontResize } from './useAutoFontResize';

import StarRating from './components/StarRating';

import './QuestionSetRewards.css';
import CheckReveal from './CheckAnimation';
import BorderBox from './BorderAnimation';
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
import Toast from "components/Common/Toast"; 


const formatDateTime = (isoStr) => {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '/');  // format: YYYY/MM/DD hh:mm:ss
};


const QuestionSetRewards = ({ t }) => {

  const { questionSetId } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  //--------------------------------------------------
  // Hide Sidebar and Footer
  //--------------------------------------------------

  useEffect(() => {
    dispatch({ type: 'SHOW_HEADER', payload: false }); // hide header
    dispatch({ type: 'SHOW_FOOTER', payload: false }); // hide footer

    var body = document.body;
    body.classList.remove("sidebar-enable");
    body.classList.remove("vertical-collpsed");

    return () => {
      dispatch({ type: 'SHOW_HEADER', payload: true }); // restore header
      dispatch({ type: 'SHOW_FOOTER', payload: true }); // restore footer
    };
  }, []);

  const checkRef = useRef();

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [myInfoQuestionSet, setMyInfoQuestionSet] = useState([]);
  const [questionSetThumbnail, setQuestionSetThumbnail] = useState({});

  const [questionSetLoading, setQuestionSetLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);  

  const [myInfoPoint, setMyInfoPoint] = useState(null);

  const [myInfoQuestionSetMasterReward, setMyInfoQuestionSetMasterReward] = useState([]);

  /* Testing Placeholder */
  useEffect(()=>{
    console.log('myInfoQuestionSet:', myInfoQuestionSet);
    /*
    if(myInfoQuestionSet?.pointReward?.hasReward){
      setMyInfoQuestionSetMasterReward(prev => ([
        {
          rewardName: `題組完成 ${myInfoQuestionSet.label}：${myInfoQuestionSet?.pointReward?.amount}點`,
          isCompleted: false,
          isRedeemed: false,
        }, 
        {
          rewardName: `題組完成 ${myInfoQuestionSet.label}：${myInfoQuestionSet?.pointReward?.amount}點`,
          isCompleted: false,
          isRedeemed: false,
        }
      ]));
    }
    */
  },[myInfoQuestionSet])  

  /* ---------- Toast ---------- */
  const [toasts, setToasts] = useState([]);   // { id, msg, type, dur }
  const pushToast = (msg, type = "success", duration = 3000) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type, duration }]);
  };
  const popToast = id => setToasts(t => t.filter(x => x.id !== id));

  //--------------------------------------------------
  // Load questionSet and columns on mount
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setQuestionSetLoading(true);
      setDataFetchError(null);

      // Run both requests in parallel
      const [questionSetResponse, rewardStatusResponse] = await Promise.all([
        axiosInstance.get(`/knovia/myInfo/${questionSetId}/getQuestionSetInfo`),
        axiosInstance.get(`/knovia/myInfo/${questionSetId}/rewardStatus`),
      ]);

      console.log('QuestionSet Loaded:', questionSetResponse.data);
      console.log('Reward Status:', rewardStatusResponse.data);

      setMyInfoQuestionSet(questionSetResponse.data);

      const rewardStatus = rewardStatusResponse.data;

      if(questionSetResponse.data?.pointReward?.hasReward && rewardStatus){
        setMyInfoQuestionSetMasterReward([
          {
            rewardName: `題組完成 ${questionSetResponse.data.label}：${questionSetResponse.data?.pointReward?.amount || 0}點`,
            isCompleted: rewardStatus.isCompleted,
            completedAt: rewardStatus.completedAt,
            isRedeemed: rewardStatus.isRedeemed,
            redeemedAt: rewardStatus.redeemedAt,
          }
        ]);
        /*
        setMyInfoQuestionSetMasterReward([
          {
            rewardName: `題組完成 ${questionSetResponse.data.label}：${questionSetResponse.data?.pointReward?.amount || 0}點`,
            isCompleted: true,
            completedAt: '2024-06-16T13:44:46.234+00:00',
            isRedeemed: rewardStatus.isRedeemed,
            redeemedAt: rewardStatus.redeemedAt,
          }
        ]);
        */
      }
      
      return {
        questionSet: questionSetResponse.data,
      };
    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load data.");
      return null; // <- This ensures the caller knows the fetch failed
    } finally {
      setQuestionSetLoading(false);
    }
  };

  //--------------------------------------------------
  // Fetch Images
  //--------------------------------------------------
  const [questionSetThumbnailUrl, setQuestionSetThumbnailUrl] = useState("");

  const fetchQuestionSetThumbnail = (qs) => {
    console.log("Start fetching questionSet thumbnail");
    console.log('qs:', qs);
    fetchThumbnailsWithCache(
      [qs],
      "infoQuestionSet",
      "sizeMedium",
      ({ id, url }) => {
        setQuestionSetThumbnailUrl(prev => url);
      },
      () => {
        console.log("QuestionSet thumbnail finished loading");
      }
    );
  };

  const loadQuestionSetData = async () => {
    const dataFetched = await fetchData();
    if (!dataFetched) return;
    const { questionSet } = dataFetched;

    if (questionSet) {
      fetchQuestionSetThumbnail(questionSet);
    }
  };

  const fetchUserPoints = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/infoPointAmount");
      console.log('[fetchUserPoints]', data);
      setMyInfoPoint(data);
    } catch (err) {
      console.error(err);
      pushToast(t("無法取得積分，請稍後再試"), "error");
    }
  };

  useEffect(() => {
    fetchUserPoints(); 
    loadQuestionSetData();
  }, [questionSetId]);



  //--------------------------------------------------
  // Redeem
  //--------------------------------------------------

  const handleRedeem = async (index) => {
    try {
      const res = await axiosInstance.post(`/knovia/myInfo/${questionSetId}/redeemReward`);
      const redeemedAt = res.data.redeemedAt;

      //const redeemedAt = '2024-06-18T13:44:46.234+00:00';

      // Immediately show the animation by toggling visible
      setMasterRewardBorderStates(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], visible: true, animate: true };
        return updated;
      });

      // After 3 seconds (match animation), update the reward state
      setTimeout(() => {
        setMasterRewardBorderStates(prev => {
          const next  = [...prev];
          next[index] = { ...next[index], animate: false };   // animation off
          return next;
        });

        setMyInfoQuestionSetMasterReward(prev => {
          const newList = [...prev];
          newList[index] = {
            ...newList[index],
            isRedeemed: true,
            redeemedAt,
          };
          return newList;
        });

        fetchUserPoints();
      }, 3000);
    } catch (error) {
      console.error('Redeem failed:', error);
      alert(error?.response?.data?.message || '兌換失敗');
    }
  };

  //--------------------------------------------------
  // Horizontal Section Menu
  //--------------------------------------------------

  const sectionMenuWrapperRef = useRef(null);
  const handleScrollLeft = (index) => {
    const wrapper = sectionMenuWrapperRef.current;
    const item = wrapper?.children[index];
    if (!wrapper || !item) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    const offsetLeft = item.offsetLeft;
    const desiredLeft = 60;

    const newScrollLeft = offsetLeft - desiredLeft;

    // Clamp value to prevent overscroll
    const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;
    wrapper.scrollTo({
      left: Math.min(Math.max(newScrollLeft, 0), maxScrollLeft),
      behavior: "smooth"
    });
  }


  //--------------------------------------------------
  // Scroll to Section
  //--------------------------------------------------
  const sectionRefs = useRef([]);
  sectionRefs.current = [...myInfoQuestionSetMasterReward].map(
    (_, i) => sectionRefs.current[i] ?? React.createRef()
  );
  const handleScrollToSection = (index, offset = 110) => {
    const sectionEl = sectionRefs.current[index]?.current;
    if (sectionEl) {
      const rect = sectionEl.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = rect.top + scrollTop - offset;

      window.scrollTo({
        top: targetY,
        behavior: "smooth",
      });
    }
  };

  const [showCheck, setShowCheck] = useState(false);
  const [showCheckWidth, setShowCheckWidth] = useState(false);

  const [borderOn, setBorderOn] = useState(false);   // <- master switch
  const [borderDivText, setBorderDivText] = useState('Border Off');
  const toggleBorderOn = () => {
    setBorderOn(prev => !prev);
    setTimeout(() => {
      if(!borderOn){
        setBorderDivText('Border On');
      } else {
        setBorderDivText('Border Off');
      }
    }, 3000)
  }

  const [masterRewardBorderStates, setMasterRewardBorderStates] = useState([]);
  useEffect(() => {
    console.log('myInfoQuestionSetMasterReward:', myInfoQuestionSetMasterReward, (myInfoQuestionSetMasterReward[0]?.isCompleted && myInfoQuestionSetMasterReward[0]?.isRedeemed) ? true : false);
    console.log('masterRewardBorderStates:', masterRewardBorderStates);
    if (myInfoQuestionSetMasterReward.length > 0) {
      setMasterRewardBorderStates(
        myInfoQuestionSetMasterReward.map((entry, index) => ({
          visible: (entry.isCompleted && entry.isRedeemed) ? true : false,
          animate : false,
          text    : entry.isCompleted
              ? (entry.isRedeemed
                   ? `已兌換\n兌換時間: ${formatDateTime(entry.redeemedAt)}`
                   : `可兌換\n完成時間: ${formatDateTime(entry.completedAt)}`
                )
              : '未完成'
        }))
      );
    }
  }, [myInfoQuestionSetMasterReward]);
  /*
  const toggleBorderAtIndex = (index) => {
    setMasterRewardBorderStates(prevStates => {
      const newStates = [...prevStates];

      const currentVisible = newStates[index].visible;
      newStates[index] = {
        ...newStates[index],
        visible: !currentVisible
      };

      // Set timeout for updating the text
      setTimeout(() => {
        setMasterRewardBorderStates(latestStates => {
          const updatedStates = [...latestStates];
          const stillVisible = updatedStates[index].visible;
          updatedStates[index] = {
            ...updatedStates[index],
            text: stillVisible ? 'Border On' : 'Border Off'
          };
          return updatedStates;
        });
      }, 3000);

      return newStates;
    });
  };
  */


  const pRef = useAutoFontResize(myInfoQuestionSet.label, 20, 12);


  //--------------------------------------------------
  // Info Points Row
  //--------------------------------------------------

  const [displayedPoints, setDisplayedPoints] = useState(myInfoPoint?.totalPoints || 0);
  const hasMounted = useRef(false);

  // Sync with props if it changes from backend
  useEffect(() => {
    if(myInfoPoint?.totalPoints) {
      if (!hasMounted.current) {
        setDisplayedPoints(myInfoPoint?.totalPoints || 0);
        hasMounted.current = true;
      } else {
        // Skip animation on first load
        setDisplayedPoints(myInfoPoint?.totalPoints || 0);
      }
    }
  }, [myInfoPoint?.totalPoints]);



  //--------------------------------------------------
  // QuestionSet Rating
  //--------------------------------------------------

  // Determine the color based on the rating value
  const getStarColor = (ratingValue) => {
    if (ratingValue <= 2) {
      return "#ff5722"; // Red for 1-2 stars
    } else if (ratingValue === 3) {
      return "#ff9800"; // Orange for 3 stars
    } else {
      return "#ffeb3b"; // Yellow for 4-5 stars
    }
  };

  const [selectedRating, setSelectedRating] = useState(null);
  const [temporateRating, setTemporateRating] = useState(null);
  const [isRatingSectionOpen, setIsRatingSectionOpen] = useState(false);
  const toggleRatingSection = ()=>{ setIsRatingSectionOpen(!isRatingSectionOpen); }

  const handleRatingSelected = (rating) => { 
    setTemporateRating(rating); 
    handleConfirmRating(rating);
  };
  const handleResetRating = () => { 
    setSelectedRating(0);
    setTemporateRating(0); 
    handleConfirmRating(0);
  };

  useEffect(()=>{
    setSelectedRating(myInfoQuestionSet.userRating);
    setTemporateRating(myInfoQuestionSet.userRating); 
  }, [myInfoQuestionSet.userRating])

  const handleConfirmRating = async (rating) => {
    if(rating>=0 && rating<=5){
      //console.log(`Confirm selecting rating: ${temporateRating} stars`);

      if(myInfoQuestionSet._id){
        // Send the updated rating to the backend
        try {
          await axiosInstance.put(`/knovia/myInfo/${questionSetId}/rateQuestionSet`, { 
            userRating: rating
          });
          setSelectedRating(rating);
          //console.log("User Rating of the question set updated successfully");
        } catch (error) {
          console.error("Error updating user question set rating", error);
        }
      }
      toggleRatingSection();
    }
  };


  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content question-set-input" 
      style={{ "--page-content-background": `url(${home4})` }}
    >

      {/* toast stack */}
      {toasts.map(t => (
        <Toast
          key={t.id}
          message={t.msg}
          type={t.type}
          duration={t.duration}
          onClose={() => popToast(t.id)}
        />
      ))}

      <Container fluid className="question-set-list-container">

        <Card className="question-set-input-card"
          style={{ minHeight: "100vh" }}
        >
          <CardBody className="question-set-input-card-body">

            {/*------ Small Screen Top Bar ------*/}
            <div className="d-block d-md-none question-set-input-title-outer-div">
              <div className="question-set-input-title-row">
                <div style={{ cursor:'pointer' }}
                  onClick={() => { navigate(`/myInfo/questionSets/${myInfoQuestionSet?._id}`) }}
                >
                  <ArrowLeft />
                </div>


                {/* Label and Description */}
                <div style={{ flex: "1", overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none", marginRight: "12px" }}
                  className="scroll-container-no-scrollbar"
                >
                  <div style={{ whiteSpace: "nowrap", userSelect: "none" }}>
                    <div className="question-set-label-text">
                      <p ref={pRef} className="mb-0" style={{ display: "inline", marginRight: "3px" }}>
                        {
                          myInfoQuestionSet?.label?.length > 18 
                          ? myInfoQuestionSet?.label.slice(0,myInfoQuestionSet?.label?.length)
                          : myInfoQuestionSet?.label
                        }
                      </p>
                    </div>
                    {myInfoQuestionSet.description && (
                      <div className="question-set-label-description">
                        <p className="mb-0" style={{ display: "inline" }}>
                          {
                            myInfoQuestionSet?.description?.length > 22 
                            ? myInfoQuestionSet?.description.slice(0,myInfoQuestionSet?.description?.length)
                            : myInfoQuestionSet?.description
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>


                {/* Tool List */}
                <div className="me-3 d-flex justify-content-end align-items-center" style={{ width: '100px' }}>
                  <div className="me-2" style={{ width: showCheckWidth ? '30px' : '0px' , height: '30px' }}>
                    <CheckReveal visible={showCheck} setVisible={setShowCheck} hasWidth={showCheckWidth} setHasWidth={setShowCheckWidth} size={30} />
                  </div>

                  <div className="question-set-top-icon-question-list" 
                    style={{ marginRight: "12.5px", cursor: "pointer" }}
                    onClick={() => { navigate(`/myInfo/questionSets/${myInfoQuestionSet?._id}`) }}
                  >
                    <ListTodo />
                  </div>

                  <div className="question-set-top-icon-message" style={{ marginRight: "12.5px", cursor: "pointer" }}>
                    <MessageSquareText />
                  </div>

                  {/* ⬇️ New Three Dots Dropdown */}
                  <UncontrolledDropdown direction="down">
                    <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
                      <MoreVertical />
                    </DropdownToggle>
                    {/* Custom styling to push the menu downward */}
                    <DropdownMenu end style={{ marginTop: '8px' }}>
                      <DropdownItem onClick={() => console.log('Edit clicked')}>
                        <Edit2 size={14} className="me-2 text-info" />
                        Edit
                      </DropdownItem>
                      <DropdownItem onClick={() => console.log('Delete clicked')}>
                        <Trash2 size={14} className="me-2 text-danger" />
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>

                </div>
              </div>

              <div className="question-set-input-section-menu">
                <div className="question-set-input-section-menu-expand">
                  <CircleChevronRight size={18}/>
                </div>
                <div className="question-set-input-section-menu-item-wrapper"
                  ref={sectionMenuWrapperRef}
                >
                  {[...myInfoQuestionSetMasterReward].map((entry, index) => {
                    return (
                      <div key={index} className={`question-set-input-section-menu-item ${activeSectionIndex===index && 'active'}`}
                        onClick={()=>{
                          setActiveSectionIndex(prev => index);
                          handleScrollLeft(index);
                          handleScrollToSection(index, 110);
                        }}
                      >
                        <p className="mb-0">{entry?.rewardName?.length > 10 ? entry?.rewardName?.slice(0,8)+'...' : entry?.rewardName}</p>
                      </div>
                    )
                  })}
                </div>
              </div>

            </div>

            {/*------ Small Screen ------*/}
            <div className="d-block d-md-none question-set-input-section-wrapper">

              {/*------ Info Point ------*/}
              <div className="d-flex d-md-none" 
                style={{ 
                  padding: "10px 20px 0px 20px",
                  justifyContent: "space-between",
                  borderBottom: "1.5px solid #e8e8f0",
                  paddingBottom: "4px"
                }}
              >
                {myInfoQuestionSetMasterReward[0]?.isCompleted ? (
                  <div>
                    {[...Array(5)].map((star, index) => {
                      const ratingValue = index + 1;

                      return (
                        <label key={index} style={{ cursor: "pointer", marginTop: "3px" }}>
                          <input
                            type="radio"
                            name="rating"
                            value={selectedRating || undefined}
                            onClick={() => {toggleRatingSection();}}
                            style={{ display: 'none' }} // Hide the radio button
                          />
                          <FaStar
                            className="star"
                            color={ratingValue <= selectedRating ? getStarColor(selectedRating) : "#e4e5e9"}
                            size={16}
                          />
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    {[...Array(5)].map((star, index) => {
                      return (
                        <label key={index} style={{ cursor: "pointer", marginTop: "3px" }}>
                          <input
                            type="radio"
                            name="rating"
                            value={selectedRating || undefined}
                            style={{ display: 'none' }} // Hide the radio button
                          />
                          <Star
                            className="star"
                            color="#8d8f94"
                            strokeWidth={1.5}
                            size={16}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', userSelect: "none", color: "#f7b900" }}>
                  <span style={{ marginRight: "1px" }}>點數：</span>
                  {hasMounted.current ? (
                    <Odometer 
                      style={{ marginTop: "0.5px", fontSize: "16px", fontWeight: "400" }}
                      value={displayedPoints}
                      format="(,ddd)"
                      duration={100}
                    />
                  ) : (
                    /* first load — plain text, no animation */
                    <span 
                      style={{ 
                        fontSize: "16px",
                        fontWeight: "400",
                        fontFamily: '"Helvetica Neue", sans-serif',
                        lineHeight: "1.1em"
                      }}
                    >
                      {displayedPoints.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {isRatingSectionOpen && (
                <div className="d-flex justify-content-center"
                  style={{
                    backgroundColor: "#f8f8f8",
                    borderBottom: "1.5px solid #e8e8f0",
                    paddingBottom: "0px"
                  }}
                >
                  <StarRating 
                    selectedRating={selectedRating}
                    getStarColor={getStarColor}
                    onRatingSelected={handleRatingSelected}
                    onResetRating={handleResetRating}
                  />
                </div>
              )}

              {!questionSetLoading && myInfoQuestionSetMasterReward.map((entry, index) => {
                return (
                  <div key={index} 
                    ref={sectionRefs.current[index]}
                    className="question-set-input-reward-outer-div"
                  >
                    <div className="question-set-input-reward-div">
                      <p className="mb-0 question-set-input-reward-title">{entry.rewardName}</p>
                    </div>

                    <div className="d-flex justify-content-center align-items-center mt-2">
                      <BorderBox
                        visible={masterRewardBorderStates[index]?.visible} // Only show animated border if redeemed
                        animate={masterRewardBorderStates[index]?.animate}
                        toggle={() => {
                          if (entry.isCompleted && !entry.isRedeemed) {
                            handleRedeem(index);
                          }
                        }}
                        text={
                          !entry.isCompleted
                            ? '未完成'
                            : entry.isRedeemed
                              ? `已兌換\n兌換時間: ${formatDateTime(entry?.redeemedAt)}`
                              : `可兌換\n完成時間: ${formatDateTime(entry?.completedAt)}`
                        }
                      />
                    </div>

                  </div>
                );
              })}

            </div>

          </CardBody>
        </Card>

      </Container>
    </div>
  );
};

export default withTranslation()(QuestionSetRewards);


